import * as types from './types'
import { LoanParams } from 'app/models/loan-params'
import { createReducer } from 'app/state/utils'

export interface LoanAmendmentState {
  loanAmendments: LoanParams[]
  loanAmendment: LoanParams | null
  facilityLoanAmendments: { data: LoanParams[]; loading: boolean }
  isLoading: boolean
}
const initialState: LoanAmendmentState = {
  loanAmendments: [],
  loanAmendment: null,
  facilityLoanAmendments: { data: [], loading: false },
  isLoading: false,
}

const loanAmendmentsReducer = createReducer(initialState)({
  [types.GET_LOAN_AMENDMENTS]: (state: LoanAmendmentState, payload: any) => {
    return { ...state, loanAmendments: payload.payload }
  },
  [types.GET_LOAN_AMENDMENT]: (state: LoanAmendmentState, payload: any) => {
    return { ...state, loanAmendment: payload.payload }
  },
  [types.GET_LOAN_AMENDMENTS_BY_FACILITY]: (
    state: LoanAmendmentState,
    payload: any,
  ) => {
    return {
      ...state,
      facilityLoanAmendments: { data: payload.payload, loading: false },
    }
  },
  [types.START_LOADING_FACILITY_LOAN_AMENDMENTS]: (
    state: LoanAmendmentState,
  ) => {
    return {
      ...state,
      facilityLoanAmendments: {
        ...state.facilityLoanAmendments,
        loading: true,
      },
    }
  },
  [types.STOP_LOADING_FACILITY_LOAN_AMENDMENTS]: (
    state: LoanAmendmentState,
  ) => {
    return {
      ...state,
      facilityLoanAmendments: {
        ...state.facilityLoanAmendments,
        loading: false,
      },
    }
  },
  [types.ADD_LOAN_AMENDMENTS]: (state: LoanAmendmentState, payload: any) => {
    const newAmendment = { ...payload.payload, status: 'Draft' }
    return {
      ...state,
      loanAmendments: [newAmendment, ...state.loanAmendments],
      // Also add to facility amendments if it matches the facility
      facilityLoanAmendments: {
        ...state.facilityLoanAmendments,
        data: [newAmendment, ...state.facilityLoanAmendments.data],
      },
    }
  },
  [types.EDIT_LOAN_AMENDMENTS]: (state: LoanAmendmentState, payload: any) => {
    const updatedAmendment = { ...payload.payload, status: 'Draft' }

    // Update in general loanAmendments array
    const index = state.loanAmendments.findIndex(
      (loanAmendment: LoanParams) => loanAmendment.id === payload.payload.id,
    )
    const newLoanAmendments =
      index !== -1
        ? [
            ...state.loanAmendments.slice(0, index),
            updatedAmendment,
            ...state.loanAmendments.slice(index + 1),
          ]
        : state.loanAmendments

    // Update in facility amendments array
    const facilityIndex = state.facilityLoanAmendments.data.findIndex(
      (loanAmendment: LoanParams) => loanAmendment.id === payload.payload.id,
    )
    const newFacilityData =
      facilityIndex !== -1
        ? [
            ...state.facilityLoanAmendments.data.slice(0, facilityIndex),
            updatedAmendment,
            ...state.facilityLoanAmendments.data.slice(facilityIndex + 1),
          ]
        : state.facilityLoanAmendments.data

    return {
      ...state,
      loanAmendments: newLoanAmendments,
      facilityLoanAmendments: {
        ...state.facilityLoanAmendments,
        data: newFacilityData,
      },
    }
  },
  [types.SUBMIT_LOAN_AMENDMENT]: (state: LoanAmendmentState, payload: any) => {
    if (payload.payload.error) {
      return state
    }

    // Update global amendments array (if the amendment exists there)
    const index = state.loanAmendments.findIndex(
      (loanAmendment: LoanParams) => loanAmendment.id === payload.payload.id,
    )
    const newArray =
      index !== -1 ? [...state.loanAmendments] : state.loanAmendments

    if (index !== -1) {
      newArray[index] = { ...newArray[index], status: 'Submitted' }
    }

    // Update facility amendments array (this is what the UI uses)
    const facilityIndex = state.facilityLoanAmendments.data.findIndex(
      (loanAmendment: LoanParams) => loanAmendment.id === payload.payload.id,
    )
    const newFacilityData =
      facilityIndex !== -1
        ? [
            ...state.facilityLoanAmendments.data.slice(0, facilityIndex),
            {
              ...state.facilityLoanAmendments.data[facilityIndex],
              status: 'Submitted',
            },
            ...state.facilityLoanAmendments.data.slice(facilityIndex + 1),
          ]
        : state.facilityLoanAmendments.data

    if (state?.loanAmendment?.id === payload.payload.id) {
      return {
        ...state,
        loanAmendment: { ...state.loanAmendment, status: 'Submitted' },
        loanAmendments: newArray,
        facilityLoanAmendments: {
          ...state.facilityLoanAmendments,
          data: newFacilityData,
        },
      }
    }
    return {
      ...state,
      loanAmendments: newArray,
      facilityLoanAmendments: {
        ...state.facilityLoanAmendments,
        data: newFacilityData,
      },
    }
  },
  [types.REJECT_LOAN_AMENDMENT]: (state: LoanAmendmentState, payload: any) => {
    if (payload.payload.error) {
      return state
    }

    // Update global amendments array (if the amendment exists there)
    const index = state.loanAmendments.findIndex(
      (loanAmendment: LoanParams) => loanAmendment.id === payload.payload.id,
    )
    const newArray =
      index !== -1 ? [...state.loanAmendments] : state.loanAmendments

    if (index !== -1) {
      newArray[index] = { ...newArray[index], status: 'Draft' }
    }

    // Update facility amendments array (this is what the UI uses)
    const facilityIndex = state.facilityLoanAmendments.data.findIndex(
      (loanAmendment: LoanParams) => loanAmendment.id === payload.payload.id,
    )
    const newFacilityData =
      facilityIndex !== -1
        ? [
            ...state.facilityLoanAmendments.data.slice(0, facilityIndex),
            {
              ...state.facilityLoanAmendments.data[facilityIndex],
              status: 'Draft',
            },
            ...state.facilityLoanAmendments.data.slice(facilityIndex + 1),
          ]
        : state.facilityLoanAmendments.data

    if (state?.loanAmendment?.id === payload?.payload?.id) {
      return {
        ...state,
        loanAmendment: { ...state.loanAmendment, status: 'Draft' },
        loanAmendments: newArray,
        facilityLoanAmendments: {
          ...state.facilityLoanAmendments,
          data: newFacilityData,
        },
      }
    }
    return {
      ...state,
      loanAmendments: newArray,
      facilityLoanAmendments: {
        ...state.facilityLoanAmendments,
        data: newFacilityData,
      },
    }
  },
  [types.APPROVE_LOAN_AMENDMENT]: (state: LoanAmendmentState, payload: any) => {
    if (payload.payload.error) {
      return state
    }
    const approvedId = payload.payload.id

    // Filter from both arrays (even if amendment doesn't exist in global array)
    const newState = {
      ...state,
      loanAmendments: state.loanAmendments.filter(
        (loanAmendment: any) => loanAmendment.id !== approvedId,
      ),
      facilityLoanAmendments: {
        ...state.facilityLoanAmendments,
        data: state.facilityLoanAmendments.data.filter(
          (loanAmendment: any) => loanAmendment.id !== approvedId,
        ),
      },
      loanAmendment:
        state.loanAmendment && state.loanAmendment.id === approvedId
          ? null
          : state.loanAmendment,
    }

    return newState
  },
  [types.DELETE_LOAN_AMENDMENT]: (state: LoanAmendmentState, payload: any) => {
    if (!payload.payload || payload.payload.length === 0) {
      return state
    }
    const deletedIds = payload.payload
      .filter((payload: any) => !payload.error)
      .map((payload: any) => payload.id)
    return {
      ...state,
      loanAmendments: state.loanAmendments.filter(
        (loanAmendment: any) => !deletedIds.includes(loanAmendment.id),
      ),
      facilityLoanAmendments: {
        ...state.facilityLoanAmendments,
        data: state.facilityLoanAmendments.data.filter(
          (loanAmendment: any) => !deletedIds.includes(loanAmendment.id),
        ),
      },
      loanAmendment:
        state.loanAmendment && deletedIds.includes(state.loanAmendment.id)
          ? null
          : state.loanAmendment,
    }
  },
  [types.START_LOADING_LOAN_AMENDMENTS]: (state: LoanAmendmentState) => {
    return { ...state, isLoading: true }
  },
  [types.STOP_LOADING_LOAN_AMENDMENTS]: (state: LoanAmendmentState) => {
    return { ...state, isLoading: false }
  },
})

export default loanAmendmentsReducer
